import React, { useCallback, useEffect, useState } from 'react';
import { ProgressBar, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import BadgeIcon from '../../../common/components/gamification/BadgeIcon';
import LevelUpModal from '../../../common/components/gamification/LevelUpModal';
import UserPageHeader from '../../components/UserPageHeader';

const BadgesPage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLevelUp, setShowLevelUp] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.gamification.getMyProfile();
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to load badges.');
        return;
      }
      const data = response.data.data || {};
      setProfile(data);
      if (data.pending_level_up && data.level_up) {
        setShowLevelUp(true);
      }
    } catch (error) {
      toast.error(`Failed to load badges: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleCloseLevelUp = async () => {
    setShowLevelUp(false);
    try {
      await api.gamification.acknowledgeLevelUp();
    } catch (error) {
      // non-blocking
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mb-4" />
          <p className="text-gray-600 text-lg font-medium">Loading badges...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
        <UserPageHeader
          iconClass="fas fa-medal"
          title="Badges"
          subtitle="Track your level, badge journey, and earned achievements"
        />
        <div className="text-center py-12 bg-white/90 rounded-2xl border border-purple-100 shadow-xl text-gray-600">
          Unable to load badge profile.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10">
      <UserPageHeader
        iconClass="fas fa-medal"
        title="Badges"
        subtitle="Track your level, badge journey, and earned achievements"
        badgeLabel={`${profile.total_points} Points`}
      />

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-xl p-4 sm:p-6 mb-4 border border-purple-100">
        <p className="text-xs font-bold text-gray-500 tracking-wider mb-3">CURRENT LEVEL</p>
        <div className="flex items-center gap-4 mb-4">
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center shadow"
            style={{ backgroundColor: `${profile.badge_color}22`, color: profile.badge_color }}
          >
            <BadgeIcon badgeKey={profile.badge_key} color={profile.badge_color} size={40} />
          </div>
          <div>
            <h5 className="font-bold text-gray-800 mb-1">{profile.current_level}</h5>
            <p className="text-sm text-gray-500 mb-0">{profile.current_badge}</p>
          </div>
        </div>
        <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill border mb-3">
          POINTS EARNED: {profile.total_points}
        </Badge>
        {profile.next_level && (
          <div>
            <div className="d-flex justify-content-between text-sm mb-1">
              <span className="text-gray-600">Progress to {profile.next_level.level_name}</span>
              <span className="font-semibold text-purple-700">{profile.progress_percent}%</span>
            </div>
            <ProgressBar now={profile.progress_percent} className="rounded-pill" style={{ height: 10 }} />
            <p className="text-xs text-gray-500 mt-2 mb-0">
              {profile.points_to_next_level} points to next level
            </p>
          </div>
        )}
      </div>

      <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-xl p-4 sm:p-6 mb-4 border border-purple-100">
        <p className="text-xs font-bold text-gray-500 tracking-wider mb-4">BADGE JOURNEY</p>
        <div className="space-y-3">
          {(profile.badge_journey || []).map((level, index) => {
            const isCompleted = level.status === 'completed';
            const isCurrent = level.status === 'current';
            const isLocked = level.status === 'locked';

            return (
              <div
                key={level.id}
                className={`flex items-center gap-3 p-3 rounded-xl border ${
                  isCurrent
                    ? 'border-green-400 bg-green-50 shadow-sm'
                    : isCompleted
                      ? 'border-purple-200 bg-purple-50/50'
                      : 'border-gray-200 bg-gray-50 opacity-70'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isLocked ? 'grayscale' : ''
                  } ${isCurrent ? 'animate-pulse' : ''}`}
                  style={{ color: level.badge_color, backgroundColor: `${level.badge_color}18` }}
                >
                  <BadgeIcon badgeKey={level.badge_key} color={level.badge_color} size={22} />
                </div>
                <div className="flex-grow-1">
                  <p className="font-semibold text-gray-800 mb-0">{level.level_name}</p>
                  <p className="text-xs text-gray-500 mb-0">{level.minimum_points} points</p>
                </div>
                <span className="text-xs font-semibold text-gray-500">#{index + 1}</span>
              </div>
            );
          })}
        </div>
      </div>

      {(profile.badges_earned || []).length > 0 && (
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-xl p-4 sm:p-6 border border-purple-100">
          <p className="text-xs font-bold text-gray-500 tracking-wider mb-4">BADGES EARNED</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {profile.badges_earned.map((badge) => (
              <div
                key={badge.id}
                className="text-center p-3 rounded-xl border border-purple-100 bg-gradient-to-b from-white to-purple-50"
              >
                <div
                  className="mx-auto mb-2 w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${badge.badge_color}22`, color: badge.badge_color }}
                >
                  <BadgeIcon badgeKey={badge.badge_key} color={badge.badge_color} size={28} />
                </div>
                <p className="text-xs font-bold text-gray-700 mb-0">{badge.level_name}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <LevelUpModal show={showLevelUp} levelUp={profile.level_up} onClose={handleCloseLevelUp} />
    </div>
  );
};

export default BadgesPage;
